/* ═══════════════════════════════════════════════════════════
   UNHUNG — COMING SOON · v3 "the mystery" · choreography
   party montage → the verdict → the door → the list
   GSAP + ScrollTrigger, word slams, custom cursor, waitlist form.
   One job: get emails. No reveal, no product — pure intrigue.
   ═══════════════════════════════════════════════════════════ */

(() => {
  "use strict";

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const coarse = window.matchMedia("(pointer: coarse)").matches;

  /* ── go-live wiring ──
     Klaviyo (Shopify email list): fill BOTH values and the form posts
     straight to the list — no backend needed.
       companyId = Klaviyo "Public API Key / Site ID" (6 chars)
       listId    = the List ID from Klaviyo → Lists & Segments → your list */
  const KLAVIYO = { companyId: "X6uLhR", listId: "Ti9ee5" };
  /* Or any custom endpoint (POST JSON {firstName,email,phone,city}).
     Both null → demo mode (localStorage). */
  const WAITLIST_ENDPOINT = null;

  /* ─── video resilience: missing clip → poster/black, no broken UI ─── */
  document.querySelectorAll("video.bgv").forEach((v) => {
    v.addEventListener("error", () => v.classList.add("is-dead"), { once: true });
  });

  /* ─── custom cursor ─── */
  if (!coarse && !reduceMotion) {
    const dot = document.getElementById("cursorDot");
    const ring = document.getElementById("cursorRing");
    let rx = innerWidth / 2, ry = innerHeight / 2;
    let tx = rx, ty = ry;
    addEventListener("mousemove", (e) => {
      tx = e.clientX; ty = e.clientY;
      dot.style.transform = `translate(${tx - 3}px, ${ty - 3}px)`;
    }, { passive: true });
    (function ringLoop() {
      rx += (tx - rx) * 0.16;
      ry += (ty - ry) * 0.16;
      ring.style.transform = `translate(${rx - 17}px, ${ry - 17}px)`;
      requestAnimationFrame(ringLoop);
    })();
    document.querySelectorAll("a, button, input").forEach((el) => {
      el.addEventListener("mouseenter", () => ring.classList.add("is-hot"));
      el.addEventListener("mouseleave", () => ring.classList.remove("is-hot"));
    });
  }

  /* ─── topbar brand: back to the top ─── */
  document.getElementById("topBrand").addEventListener("click", (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  });

  /* ─── party montage: autoplay is a promise, not a right ─── */
  const vidParty = document.getElementById("vidParty");
  const isPortrait = matchMedia("(orientation: portrait)").matches;
  if (isPortrait) {
    /* phones get the AI-reframed vertical cut, not a crop */
    vidParty.src = window.UNHUNG_ASSETS.party916;
  }
  const kickParty = () => {
    if (vidParty.paused && !vidParty.classList.contains("is-dead")) {
      vidParty.play().catch(() => {/* first frame carries it */});
    }
  };
  kickParty();
  vidParty.addEventListener("canplay", kickParty, { once: true });
  addEventListener("pointerdown", kickParty, { once: true });
  addEventListener("scroll", kickParty, { once: true, passive: true });
  vidParty.addEventListener("pause", () => { if (!document.hidden) setTimeout(kickParty, 150); });
  document.addEventListener("visibilitychange", () => { if (!document.hidden) kickParty(); });

  /* ─── VIP background montage: same film, offset so it never mirrors the hero ─── */
  const vidVip = document.getElementById("vidVip");
  if (isPortrait) vidVip.src = window.UNHUNG_ASSETS.party916;
  const kickVip = () => {
    if (vidVip.paused && !vidVip.classList.contains("is-dead")) {
      vidVip.play().catch(() => {});
    }
  };
  const offsetVip = () => { try { if (vidVip.currentTime < 2) vidVip.currentTime = 6.5; } catch (_) {} };
  if (vidVip.readyState >= 1) offsetVip();
  else vidVip.addEventListener("loadedmetadata", offsetVip, { once: true });
  kickVip();
  vidVip.addEventListener("canplay", kickVip, { once: true });
  addEventListener("pointerdown", kickVip, { once: true });
  addEventListener("scroll", kickVip, { once: true, passive: true });
  vidVip.addEventListener("pause", () => { if (!document.hidden) setTimeout(kickVip, 150); });
  document.addEventListener("visibilitychange", () => { if (!document.hidden) kickVip(); });

  /* ─── entry sequence: the type develops like a photograph ─── */
  const veil = document.getElementById("veil");
  let enterTL = null;
  /* split display words into characters (brand-mark spans ride along whole) */
  const splitChars = (el) => {
    const out = [];
    [...el.childNodes].forEach((node) => {
      if (node.nodeType === 3) {
        const frag = document.createDocumentFragment();
        [...node.textContent].forEach((c) => {
          if (c === " ") { frag.appendChild(document.createTextNode(" ")); return; }
          const s = document.createElement("span");
          s.className = "ch";
          s.textContent = c;
          out.push(s);
          frag.appendChild(s);
        });
        node.replaceWith(frag);
      } else if (node.nodeType === 1) {
        node.classList.add("ch");
        out.push(node);
      }
    });
    return out;
  };
  if (!reduceMotion) {
    splitChars(document.querySelector("#partyQ .pl-mega"));
    splitChars(document.querySelector("#partyA .pl-mega"));
    gsap.set("#partyA .pl-kicker, #partyA .pl-mega .ch", { opacity: 0 });
  }

  function enter() {
    if (reduceMotion) { veil.remove(); return; }
    gsap.set("#partyQ .pl-kicker", { opacity: 0 });
    gsap.set("#partyQ .pl-mega .ch", { opacity: 0, y: 44, filter: "blur(22px)" });
    enterTL = gsap.timeline()
      .to(veil, { opacity: 0, duration: 0.9, ease: "power2.inOut", delay: 1.1,
                  onComplete: () => veil.remove() })
      /* the kicker breathes in, tracking tightening into place */
      .fromTo("#partyQ .pl-kicker",
        { opacity: 0, letterSpacing: "1.5em", filter: "blur(7px)" },
        /* phones need the eyebrow brighter to stay legible over footage */
        { opacity: isPortrait ? 0.88 : 0.68, letterSpacing: "0.6em", filter: "blur(0px)", duration: 1.7, ease: "power2.out" }, "-=0.35")
      /* the word develops, letter by letter — translucent, letting the film breathe through */
      .to("#partyQ .pl-mega .ch", {
        opacity: 0.85, y: 0, filter: "blur(0px)",
        duration: 1.3, ease: "power3.out", stagger: 0.085,
      }, "-=1.3")
      /* the question mark catches the light — once */
      .fromTo("#partyQ .q-lime",
        { textShadow: "0 0 0px rgba(212,234,0,0)" },
        { textShadow: "0 0 42px rgba(212,234,0,1)", duration: 0.5, ease: "power2.in", yoyo: true, repeat: 1 }, "-=0.25");
  }
  if (document.readyState === "complete") enter();
  else addEventListener("load", enter, { once: true });
  /* hard fallback: rAF-driven tweens can stall in hidden/throttled tabs —
     never leave a visitor stranded behind the veil */
  setTimeout(() => {
    const v = document.getElementById("veil");
    if (v) {
      v.style.transition = "opacity 0.6s ease";
      v.style.opacity = "0";
      setTimeout(() => v.remove(), 700);
    }
  }, 4500);

  if (reduceMotion) { initForm(); initTurntable(); return; }

  gsap.registerPlugin(ScrollTrigger);

  /* a slam: accelerates in from huge, stops dead */
  const slamIn = (tl, targets, at, opts = {}) => {
    tl.fromTo(targets,
      { opacity: 0, scale: opts.from ?? 3, yPercent: -6 },
      {
        opacity: 1, scale: 1, yPercent: 0,
        ease: "expo.in",
        duration: opts.dur ?? 0.05,
        stagger: opts.stagger ?? 0,
      }, at);
  };

  /* ═══ ACT 1 · THE PARTY — question dissolves, answer rises, lights out ═══ */
  const partyTL = gsap.timeline({
    scrollTrigger: {
      trigger: "#act-party",
      start: "top top",
      end: "+=300%",
      pin: true,
      scrub: 0.5,
      /* if the visitor scrolls mid-intro, finish the intro instantly so
         scrubbed exits always capture the settled state (scroll-back-up
         then restores the text correctly) */
      onUpdate: (self) => {
        if (enterTL && self.progress > 0.03 && enterTL.progress() < 1) enterTL.progress(1);
      },
    },
  });
  partyTL
    .to("#scrollCue", { opacity: 0, duration: 0.04 }, 0.01)
    /* the question evaporates — letters drift up and blur into air */
    .to("#partyQ .pl-mega .ch", {
      opacity: 0, y: -40, filter: "blur(16px)",
      stagger: 0.02, duration: 0.14, ease: "power1.in",
    }, 0.14)
    .to("#partyQ .pl-kicker", {
      opacity: 0, letterSpacing: "1.4em", filter: "blur(8px)",
      duration: 0.16, ease: "power1.in",
    }, 0.14)
    /* the answer develops over the chaos */
    .fromTo("#partyA .pl-kicker",
      { opacity: 0, letterSpacing: "1.5em", filter: "blur(7px)" },
      { opacity: isPortrait ? 0.88 : 0.68, letterSpacing: "0.6em", filter: "blur(0px)", duration: 0.12, ease: "power2.out", immediateRender: false }, 0.38)
    .fromTo("#partyA .pl-mega .ch",
      { opacity: 0, y: 40, filter: "blur(20px)" },
      { opacity: 0.85, y: 0, filter: "blur(0px)", stagger: 0.018, duration: 0.12, ease: "power3.out", immediateRender: false }, 0.42)
    .to("#act-party [data-shade]", { opacity: 1, duration: 0.15 }, 0.64)
    /* and evaporates in its own time */
    .to("#partyA .pl-mega .ch", {
      opacity: 0, y: -36, filter: "blur(16px)",
      stagger: 0.018, duration: 0.12, ease: "power1.in",
    }, 0.84)
    .to("#partyA .pl-kicker", {
      opacity: 0, letterSpacing: "1.4em", duration: 0.13, ease: "power1.in",
    }, 0.84)
    .to("#vidParty", { opacity: 0.05, duration: 0.13 }, 0.86)
    .set("#act-party", { autoAlpha: 1 }, 1);

  /* ═══ ACT 2 · THE VERDICT — three slams on pure black ═══ */
  const verdictTL = gsap.timeline({
    scrollTrigger: {
      trigger: "#act-verdict",
      start: "top top",
      end: "+=340%",
      pin: true,
      scrub: 0.5,
    },
  });
  /* one word owns the screen per scroll step; each pushes past the lens
     as the next slams in behind it */
  slamIn(verdictTL, "#vw1", 0.06, { from: 2.4, dur: 0.05 });
  verdictTL.to("#vw1", { opacity: 0, scale: 1.6, ease: "power2.in", duration: 0.04 }, 0.28);
  slamIn(verdictTL, "#vw2", 0.34, { from: 3.4, dur: 0.06 });
  verdictTL.to("#vw2", { opacity: 0, scale: 1.6, ease: "power2.in", duration: 0.04 }, 0.56);
  slamIn(verdictTL, "#vw3", 0.62, { from: 3.4, dur: 0.06 });
  verdictTL.to("#verdictStage", { opacity: 0, scale: 1.06, ease: "power2.in", duration: 0.08 }, 0.9);

  /* ═══ ACT 3 · THE DOOR — urgency before the ask ═══ */
  const doorTL = gsap.timeline({
    scrollTrigger: {
      trigger: "#act-door",
      start: "top top",
      end: "+=220%",
      pin: true,
      scrub: 0.5,
    },
  });
  const doorBeat = (sel, at, out) => {
    slamIn(doorTL, sel, at, { from: 2.6, dur: 0.05 });
    if (out) {
      doorTL.to(sel, {
        opacity: 0, y: -34, filter: "blur(10px)",
        ease: "power1.in", duration: 0.07,
      }, out);
    }
  };
  doorBeat("#dl1", 0.06, 0.3);
  doorBeat("#dl2", 0.38, 0.62);
  doorBeat("#dl3", 0.7, null); /* holds — the ask is next */
  doorTL.set("#act-door", { autoAlpha: 1 }, 1);

  /* ═══ ACT 4 · VIP — the card cascades in once ═══ */
  gsap.timeline({
    scrollTrigger: {
      trigger: "#vip",
      start: "top 70%",
      once: true,
    },
  })
    .from(".vip-kicker", { opacity: 0, x: -44, duration: 0.7, ease: "power3.out" })
    .from(".vip-title", { opacity: 0, y: 42, duration: 0.8, ease: "power3.out" }, "-=0.45")
    .from("#vipForm > *", { opacity: 0, y: 26, stagger: 0.07, duration: 0.6, ease: "power2.out" }, "-=0.5");

  /* ═══ GUIDED SCROLL — every gesture lands on the next story beat ═══
     Fast scrollers were blowing through the acts and missing the story.
     Each wheel tick / swipe / key advances exactly one beat; the journey
     between beats plays as an animated transition. The VIP form releases
     back to native scrolling. */
  const beatDefs = [
    ["act-party", [0, 0.5]],
    ["act-verdict", [0.16, 0.45, 0.75]],
    ["act-door", [0.16, 0.47, 0.8]],
  ];
  const getBeats = () => {
    const out = [];
    beatDefs.forEach(([id, fractions]) => {
      const st = ScrollTrigger.getAll().find((s) => s.pin && s.trigger && s.trigger.id === id);
      if (st) fractions.forEach((f) => out.push(Math.round(st.start + (st.end - st.start) * f)));
    });
    out.push(Math.round(document.getElementById("vip").getBoundingClientRect().top + scrollY));
    return out.sort((a, b) => a - b);
  };
  let beatAnimating = false;
  const vipReleaseY = () => getBeats()[getBeats().length - 1];
  const gateActive = () => scrollY < vipReleaseY() - 10;
  const goToBeat = (dir) => {
    if (beatAnimating) return;
    const beats = getBeats();
    const y = scrollY;
    let target;
    if (dir > 0) {
      target = beats.find((b) => b > y + 10);
    } else {
      const prev = beats.filter((b) => b < y - 10);
      target = prev[prev.length - 1];
    }
    if (target == null) return;
    beatAnimating = true;
    const state = { y };
    const dur = gsap.utils.clamp(0.7, 1.9, Math.abs(target - y) / 900);
    gsap.to(state, {
      y: target, duration: dur, ease: "power2.inOut",
      onUpdate: () => window.scrollTo(0, state.y),
      onComplete: () => { setTimeout(() => { beatAnimating = false; }, 140); },
    });
  };
  addEventListener("wheel", (e) => {
    if (!gateActive() && e.deltaY > 0) return; /* free below the gate, going down */
    if (!gateActive() && scrollY > vipReleaseY() + 10) return; /* deep in form area */
    e.preventDefault();
    if (beatAnimating || Math.abs(e.deltaY) < 8) return;
    goToBeat(e.deltaY > 0 ? 1 : -1);
  }, { passive: false });
  let touchY = null;
  addEventListener("touchstart", (e) => { touchY = e.touches[0].clientY; }, { passive: true });
  addEventListener("touchmove", (e) => {
    if (gateActive() || beatAnimating) e.preventDefault();
  }, { passive: false });
  addEventListener("touchend", (e) => {
    if (touchY == null) return;
    const delta = touchY - e.changedTouches[0].clientY;
    touchY = null;
    if (!gateActive() && delta > 0) return;
    if (!gateActive() && scrollY > vipReleaseY() + 10) return;
    if (Math.abs(delta) < 30) return;
    goToBeat(delta > 0 ? 1 : -1);
  }, { passive: true });
  addEventListener("keydown", (e) => {
    if (/INPUT|TEXTAREA/.test(document.activeElement.tagName)) return;
    if (!gateActive()) return;
    if (["ArrowDown", "PageDown", " "].includes(e.key)) { e.preventDefault(); goToBeat(1); }
    if (["ArrowUp", "PageUp"].includes(e.key)) { e.preventDefault(); goToBeat(-1); }
  });

  initTurntable();
  initForm();

  /* ═══ turntable — 96-frame canvas spin behind the VIP card ═══ */
  function initTurntable() {
    const canvas = document.getElementById("turntable");
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const FRAMES = 96;
    const frames = new Array(FRAMES);
    let loaded = 0, started = false, frame = 0;

    function draw() {
      const img = frames[frame];
      if (!img) return;
      const dpr = Math.min(devicePixelRatio || 1, 2);
      const w = canvas.clientWidth, h = canvas.clientHeight;
      if (canvas.width !== w * dpr) { canvas.width = w * dpr; canvas.height = h * dpr; }
      const s = Math.max((w * dpr) / img.width, (h * dpr) / img.height) * 0.82;
      const dw = img.width * s, dh = img.height * s;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
    }

    function spin() {
      if (reduceMotion) { draw(); return; }
      frame = (frame + 1) % FRAMES;
      draw();
      setTimeout(() => requestAnimationFrame(spin), 84);
    }

    function load() {
      if (started) return;
      started = true;
      for (let i = 0; i < FRAMES; i++) {
        const img = new Image();
        img.src = `assets/seq/unhung-turn-${String(i).padStart(3, "0")}.webp`;
        img.onload = () => { frames[i] = img; if (++loaded === 1) draw(); if (loaded === FRAMES) spin(); };
        img.onerror = () => { if (++loaded === FRAMES && frames.some(Boolean)) spin(); };
      }
    }

    new IntersectionObserver((entries, obs) => {
      if (entries.some((e) => e.isIntersecting)) { load(); obs.disconnect(); }
    }, { rootMargin: "600px" }).observe(canvas);
    addEventListener("resize", draw, { passive: true });
  }

  /* ═══ VIP waitlist form ═══ */
  function initForm() {
    const form = document.getElementById("vipForm");
    const btn = document.getElementById("vipBtn");
    const success = document.getElementById("vipSuccess");

    const err = (id, msg) => {
      const input = document.getElementById(id);
      const slot = form.querySelector(`[data-err="${id}"]`);
      input.classList.toggle("is-bad", !!msg);
      slot.textContent = msg || "";
      slot.classList.toggle("show", !!msg);
      return !msg;
    };

    const validators = {
      fName: (v) => (v.trim().length >= 2 ? "" : "Tell us who's coming."),
      fEmail: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()) ? "" : "That email won't survive the night."),
      fPhone: (v) => (!v.trim() || /^[+()\-.\s\d]{7,20}$/.test(v.trim()) ? "" : "That number looks off."),
      fCity: () => "",
    };

    Object.keys(validators).forEach((id) => {
      const input = document.getElementById(id);
      input.addEventListener("blur", () => err(id, validators[id](input.value)));
      input.addEventListener("input", () => input.classList.contains("is-bad") && err(id, validators[id](input.value)));
    });

    async function submit(data) {
      if (KLAVIYO.companyId && KLAVIYO.listId) {
        /* Klaviyo requires E.164 phones; send only if it qualifies */
        const e164 = data.phone.replace(/[()\-.\s]/g, "");
        const attrs = {
          email: data.email.trim(),
          first_name: data.firstName.trim(),
        };
        if (/^\+\d{8,15}$/.test(e164)) attrs.phone_number = e164;
        if (data.city.trim()) attrs.location = { city: data.city.trim() };
        const res = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO.companyId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", revision: "2024-10-15" },
            body: JSON.stringify({
              data: {
                type: "subscription",
                attributes: { profile: { data: { type: "profile", attributes: attrs } } },
                relationships: { list: { data: { type: "list", id: KLAVIYO.listId } } },
              },
            }),
          }
        );
        if (!res.ok && res.status !== 202) throw new Error(`klaviyo ${res.status}`);
        return { ok: true };
      }
      if (WAITLIST_ENDPOINT) {
        const res = await fetch(WAITLIST_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error(`waitlist endpoint ${res.status}`);
        return res.json().catch(() => ({}));
      }
      /* demo mode: persist locally, keep the contract async */
      const list = JSON.parse(localStorage.getItem("unhung-vip") || "[]");
      list.push({ ...data, ts: new Date().toISOString() });
      localStorage.setItem("unhung-vip", JSON.stringify(list));
      return new Promise((r) => setTimeout(() => r({ ok: true, demo: true }), 900));
    }

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const values = {
        firstName: document.getElementById("fName").value,
        email: document.getElementById("fEmail").value,
        phone: document.getElementById("fPhone").value,
        city: document.getElementById("fCity").value,
      };
      const ok = ["fName", "fEmail", "fPhone", "fCity"]
        .map((id) => err(id, validators[id](document.getElementById(id).value)))
        .every(Boolean);
      if (!ok) return;

      btn.classList.add("is-busy");
      btn.querySelector("span").textContent = "Securing your spot…";
      try {
        await submit(values);
        form.hidden = true;
        success.hidden = false;
        if (!reduceMotion && window.gsap) {
          gsap.fromTo(success, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" });
          gsap.fromTo(".vs-flash", { scale: 0.4, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.7, ease: "back.out(2)", delay: 0.15 });
        }
      } catch (_) {
        btn.classList.remove("is-busy");
        btn.querySelector("span").textContent = "Get on the VIP List";
        err("fEmail", "Something broke on our end — try again.");
      }
    });
  }
})();
